import { useState } from 'react'
import { OwnerTable, type ColumnDef } from './OwnerTable'
import { OwnerCard } from './OwnerCard'
import { OwnerEmptyScaffold } from './OwnerEmptyScaffold'
import { OwnerSoftConfirm, OwnerFirmConfirm, OwnerHardConfirm } from './OwnerConfirm'
import { OwnerCommandPalette, OwnerShortcutsSheet } from './OwnerCommandPalette'
import ScienceRoundedIcon from '@mui/icons-material/ScienceRounded'
import './ownerKit.css'

type SampleRow = {
  id: string
  name: string
  plan: 'free' | 'pro' | 'enterprise'
  status: 'active' | 'suspended' | 'pending'
  credits: number
  mrr: number
}

const SAMPLE_ROWS: SampleRow[] = [
  { id: 'ws-001', name: 'Acme Studio', plan: 'enterprise', status: 'active', credits: 14200, mrr: 1200 },
  { id: 'ws-002', name: 'Starlight Labs', plan: 'pro', status: 'active', credits: 3500, mrr: 450 },
  { id: 'ws-003', name: 'Hyperion Design', plan: 'free', status: 'pending', credits: 100, mrr: 0 },
  { id: 'ws-004', name: 'Nexus Dynamics', plan: 'enterprise', status: 'active', credits: 8900, mrr: 2400 },
  { id: 'ws-005', name: 'Orbit Health', plan: 'pro', status: 'suspended', credits: 0, mrr: 450 },
]

export function OwnerKitPlayground() {
  const [selectedRow, setSelectedRow] = useState<SampleRow | null>(null)
  const [firmOpen, setFirmOpen] = useState(false)
  const [hardOpen, setHardOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const [lastAction, setLastAction] = useState<string>('None yet')

  const columns: ColumnDef<SampleRow>[] = [
    { key: 'id', header: 'Workspace ID', sortable: true, accessor: (r) => r.id, width: 120 },
    { key: 'name', header: 'Workspace Name', sortable: true, accessor: (r) => r.name },
    {
      key: 'plan',
      header: 'Plan Tier',
      sortable: true,
      accessor: (r) => r.plan,
      render: (r) => (
        <span
          className="ownerIdentityRole"
          style={{
            background: r.plan === 'enterprise' ? 'rgba(192, 122, 46, 0.2)' : 'rgba(91, 141, 239, 0.16)',
            color: r.plan === 'enterprise' ? 'var(--warn)' : 'var(--accent-hi)',
          }}
        >
          {r.plan}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      accessor: (r) => r.status,
      render: (r) => (
        <span style={{ color: r.status === 'active' ? 'var(--ok)' : r.status === 'suspended' ? 'var(--danger)' : 'var(--text-low)' }}>
          ● {r.status}
        </span>
      ),
    },
    { key: 'credits', header: 'Credits', sortable: true, accessor: (r) => r.credits },
    { key: 'mrr', header: 'MRR ($)', sortable: true, accessor: (r) => r.mrr },
  ]

  const tokenSwatches = [
    { name: '--surface-0', value: '#0b0f14', label: 'Page Background' },
    { name: '--surface-1', value: '#12171f', label: 'Panel Background' },
    { name: '--surface-2', value: '#1a2029', label: 'Card Background' },
    { name: '--surface-3', value: '#232b36', label: 'Elevated / Hover' },
    { name: '--divider', value: '#232b36', label: 'Borders & Rules' },
    { name: '--text-hi', value: '#e6ebf2', label: 'Primary Text' },
    { name: '--text-mid', value: '#a3afbf', label: 'Secondary Text' },
    { name: '--text-low', value: '#67748a', label: 'Muted Metadata' },
    { name: '--accent', value: '#5b8def', label: 'Accent / Links' },
    { name: '--accent-hi', value: '#8dabf7', label: 'Accent Hover' },
    { name: '--ok', value: '#3fb27f', label: 'Healthy / OK' },
    { name: '--warn', value: '#e6b45c', label: 'Warning / Review' },
    { name: '--danger', value: '#e26b6b', label: 'Destructive / Danger' },
    { name: '--info', value: '#7ba7d9', label: 'Info / Neutral' },
    { name: '--env-sit', value: '#c07a2e', label: 'Env: SIT' },
    { name: '--env-dev', value: '#c9a83a', label: 'Env: DEV' },
    { name: '--env-prod', value: '#c1445a', label: 'Env: PROD' },
  ]

  return (
    <div className="ownerPlayground" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '32px', maxWidth: '1200px' }}>
      <div>
        <span className="ownerEyebrow">Design System & Verification</span>
        <h1 style={{ fontSize: 'var(--fs-hero)', margin: '4px 0 8px', color: 'var(--text-hi)' }}>
          Design Kit Playground (/config/playground)
        </h1>
        <p style={{ color: 'var(--text-mid)', fontSize: 'var(--fs-body)' }}>
          Interactive visual verification surface for tokens, tables, cards, confirm treatments, empty scaffolds, and the command palette.
        </p>
      </div>

      {/* 1. Tokens Matrix */}
      <section style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <h2 style={{ fontSize: 'var(--fs-h2)', margin: 0, color: 'var(--text-hi)' }}>1. Palette & Design Tokens</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '10px' }}>
          {tokenSwatches.map((swatch) => (
            <div
              key={swatch.name}
              style={{
                background: 'var(--surface-1)',
                border: '1px solid var(--divider)',
                borderRadius: 'var(--radius-sm)',
                padding: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <div
                style={{
                  height: '36px',
                  borderRadius: 'var(--radius-sm)',
                  background: `var(${swatch.name})`,
                  border: '1px solid var(--divider)',
                }}
              />
              <span style={{ fontSize: 'var(--fs-tiny)', fontWeight: 700, color: 'var(--text-hi)' }}>{swatch.name}</span>
              <span style={{ fontSize: 'var(--fs-tiny)', color: 'var(--text-low)' }}>{swatch.value} · {swatch.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* 2. Generic Table Demo */}
      <section style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: 'var(--fs-h2)', margin: 0, color: 'var(--text-hi)' }}>
            2. Generic Table (&lt;OwnerTable&gt;)
          </h2>
          <span style={{ fontSize: 'var(--fs-meta)', color: 'var(--text-low)' }}>
            Selected: {selectedRow ? selectedRow.name : 'None'}
          </span>
        </div>
        <OwnerTable
          data={SAMPLE_ROWS}
          columns={columns}
          viewKey="playground-demo"
          onRowClick={(row) => setSelectedRow(row)}
          actionsMenu={(row) => (
            <button
              type="button"
              className="ownerCardMenuBtn"
              onClick={() => setLastAction(`Clicked action menu for ${row.name}`)}
            >
              ⋯
            </button>
          )}
        />
      </section>

      {/* 3. Cards Demo */}
      <section style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <h2 style={{ fontSize: 'var(--fs-h2)', margin: 0, color: 'var(--text-hi)' }}>3. Card Treatments (&lt;OwnerCard&gt;)</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
          <OwnerCard
            title="Workspace Operational Health"
            metaLine="Acme Studio · plan: Enterprise · active 12m ago"
            primaryAction={
              <button
                type="button"
                className="ownerModalBtn ownerModalBtn--primary"
                onClick={() => setLastAction('Card primary clicked')}
              >
                Inspect Health
              </button>
            }
            secondaryAction={
              <button
                type="button"
                className="ownerModalBtn ownerModalBtn--default"
                onClick={() => setLastAction('Card secondary clicked')}
              >
                Download Log
              </button>
            }
            metaChips={[
              <span key="1" className="ownerIdentityRole">LIVE</span>,
              <span key="2" style={{ color: 'var(--ok)', fontSize: 'var(--fs-tiny)' }}>● 99.9%</span>,
            ]}
          >
            <p style={{ margin: 0, color: 'var(--text-mid)', fontSize: 'var(--fs-body)' }}>
              Card content has an internal max-height with smooth vertical scrolling to prevent panels from stretching out of shape.
            </p>
            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ padding: '8px', background: 'var(--surface-0)', borderRadius: 'var(--radius-sm)' }}>
                <strong>Firestore IO:</strong> 12,400 reads / hour (within quota)
              </div>
              <div style={{ padding: '8px', background: 'var(--surface-0)', borderRadius: 'var(--radius-sm)' }}>
                <strong>Functions latency:</strong> 142ms p95
              </div>
            </div>
          </OwnerCard>

          <OwnerCard
            title="Internal Scroll Demonstration"
            metaLine="Showing fixed max-height compliance"
            maxBodyHeight={140}
          >
            <p>Line 1 — Testing internal card scroll boundary.</p>
            <p>Line 2 — Cards must never blow out panel dimensions.</p>
            <p>Line 3 — Content overflows with internal scrollbar.</p>
            <p>Line 4 — Even with extensive telemetry records.</p>
            <p>Line 5 — Bottom reached.</p>
          </OwnerCard>
        </div>
      </section>

      {/* 4. Confirm Treatments Demo */}
      <section style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <h2 style={{ fontSize: 'var(--fs-h2)', margin: 0, color: 'var(--text-hi)' }}>
          4. Confirm Treatments (&lt;OwnerConfirm&gt; 3 Tiers)
        </h2>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            background: 'var(--surface-1)',
            border: '1px solid var(--divider)',
            borderRadius: 'var(--radius-md)',
            padding: '16px',
          }}
        >
          <div>
            <span style={{ fontSize: 'var(--fs-tiny)', color: 'var(--text-low)', display: 'block' }}>Tier 1: Soft (Inline 3s)</span>
            <OwnerSoftConfirm
              label="Reset Filter Cache"
              onConfirm={() => setLastAction('Soft confirm executed')}
            />
          </div>

          <div style={{ borderLeft: '1px solid var(--divider)', paddingLeft: '16px' }}>
            <span style={{ fontSize: 'var(--fs-tiny)', color: 'var(--text-low)', display: 'block' }}>Tier 2: Firm (Modal + Reason)</span>
            <button
              type="button"
              className="ownerModalBtn ownerModalBtn--default"
              onClick={() => setFirmOpen(true)}
            >
              Grant 5,000 Credits…
            </button>
          </div>

          <div style={{ borderLeft: '1px solid var(--divider)', paddingLeft: '16px' }}>
            <span style={{ fontSize: 'var(--fs-tiny)', color: 'var(--text-low)', display: 'block' }}>Tier 3: Hard (Destructive Echo)</span>
            <button
              type="button"
              className="ownerModalBtn ownerModalBtn--danger"
              onClick={() => setHardOpen(true)}
            >
              Purge Workspace…
            </button>
          </div>

          <span style={{ marginLeft: 'auto', fontSize: 'var(--fs-meta)', color: 'var(--text-low)' }}>
            Last Action: <strong>{lastAction}</strong>
          </span>
        </div>
      </section>

      {/* 5. Universal Empty Scaffold Demo */}
      <section style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <h2 style={{ fontSize: 'var(--fs-h2)', margin: 0, color: 'var(--text-hi)' }}>
          5. Universal Empty Scaffold (&lt;OwnerEmptyScaffold&gt;)
        </h2>
        <OwnerEmptyScaffold
          icon={<ScienceRoundedIcon fontSize="medium" />}
          explanation="No experimental features currently flagged for this workspace."
          configLabel="Configure experimental flags"
          onConfigClick={() => setLastAction('Navigated to config flags')}
          howDataGetsHere={[
            'Engineers flag experimental capabilities via remote config.',
            'Workspaces opted into preview cohorts receive flags automatically.',
            'Manual overrides can be enabled through the Flags manager.',
          ]}
        />
      </section>

      {/* 6. Command Palette & Shortcuts Demo */}
      <section style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <h2 style={{ fontSize: 'var(--fs-h2)', margin: 0, color: 'var(--text-hi)' }}>
          6. Command Palette & Shortcuts
        </h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            type="button"
            className="ownerModalBtn ownerModalBtn--primary"
            onClick={() => setPaletteOpen(true)}
          >
            Open Command Palette (⌘K)
          </button>
          <button
            type="button"
            className="ownerModalBtn ownerModalBtn--default"
            onClick={() => setShortcutsOpen(true)}
          >
            Show Shortcuts Sheet (?)
          </button>
        </div>
      </section>

      {/* Modals */}
      <OwnerFirmConfirm
        isOpen={firmOpen}
        title="Grant 5,000 Workspace Credits"
        description="Grants owner-issued credits to Acme Studio wallet. Requires an operational justification for auditing."
        onClose={() => setFirmOpen(false)}
        onConfirm={(reason) => {
          setFirmOpen(false)
          setLastAction(`Granted credits with reason: ${reason}`)
        }}
      />

      <OwnerHardConfirm
        isOpen={hardOpen}
        title="Permanently Purge Workspace"
        targetHumanName="Acme Studio"
        description="This will permanently delete all boards, goals, and uploaded assets. This mutation is irreversible."
        onClose={() => setHardOpen(false)}
        onConfirm={(reason) => {
          setHardOpen(false)
          setLastAction(`Purged Acme Studio with reason: ${reason}`)
        }}
      />

      <OwnerCommandPalette
        isOpen={paletteOpen}
        onClose={() => setPaletteOpen(false)}
      />

      <OwnerShortcutsSheet
        isOpen={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
      />
    </div>
  )
}
